import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Device from 'device/device';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _findIndex from 'lodash/findIndex';
import _cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Messages, MediaPlayerEvents } from 'mlb-media-player';
import {
    PlayerControls,
    ControlsEvents,
} from 'mlb-player-controls-react';
import { AdvertisingPluginEvents } from 'mlb-player-plugins';
import MediaPlayer from '../../components/media-player/media-player';
import LeaveBehind from '../../components/leave-behind/leave-behind';
import MLBMediaItem from '../../models/mlb-media-item';
import Playlist from '../../models/playlist';
import PluginConfigHelper from '../../utils/config/plugin-config-helper';
import OverlayTypes from "../../enum/overlay-types";
import LeaveBehindTypes from "../../enum/leave-behind-types";
import ClickToPlayOverlay from '../../components/overlay/click-to-play';
import LeaveBehindOverlay from '../../components/overlay/leave-behind';
import EmbedEvents from '../../enum/embed-events';
import PlayerSizes from '../../enum/player-sizes';
import './app.scss';
import html5PlayerEvents from 'mlb-media-player/src/js/events/html5-player-events';

class App extends Component {

    constructor(props) {

        super(props);

        const {
            initialMedia,
            list,
            requestParams,
            topicId,
        } = this.props;
        const { query } = requestParams;
        const playlistData = _cloneDeep(list);

        const initialMediaMetaData = this.findInitialMediaMetadata(initialMedia);
        const initialContentId = this.findInitialContentId(initialMediaMetaData);

        // Add the initial media to the list of media so we have a valid playlist
        playlistData.unshift(initialMediaMetaData);

        this.deviceInfo = undefined;
        this.autoPlay = undefined;
        this.mute = _get(query, 'mute', false);
        this.observationCenter = undefined;
        this.embedSubject = new BehaviorSubject({ type: 'empty' });
        this.mediaConfig = this.setUpMediaConfig();
        this.getNextItemMetadataOverride = _get(this.mediaConfig, 'getNextItemMetadataOverride');
        this.playlist = this.createPlaylist(playlistData, initialContentId, topicId);

        this.mediaPlayerRef = React.createRef();
        this.lbType = _get(query, 'lb');
        this.leaveBehindEnabled = _get(query, 'leaveBehindEnabled', false);
        this.autoPlayParam = _get(query, 'autoPlay') || _get(query, 'autoplay', false);

        this.initAutoPlay();
        this.initDevice();

        const clip = this.playlist.getSelectedItem();

        this.state = {
            showOverlay: !this.autoPlay,
            overlayType: OverlayTypes.CLICK_TO_PLAY,
            mediaPlayer: undefined,
            clip,
            showControls: false,
            playerSize: PlayerSizes.SM,
            sentTracking: false,
            wasClickToPlay: false,
        };

        this.onClickToPlay = this.onClickToPlay.bind(this);
        this.onMediaPlayerReady = this.onMediaPlayerReady.bind(this);
        this.onMediaComplete = this.onMediaComplete.bind(this);
        this.onCountdownComplete = this.onCountdownComplete.bind(this);
        this.onClickReplayClip = this.onClickReplayClip.bind(this);
        this.onClickNextClip = this.onClickNextClip.bind(this);

        this.subscriptions = [];

    }

    componentDidMount() {

        if (!this.state.sentTracking) {

            this.setState({
                sentTracking: true,
            });

        }

    }

    componentDidUpdate(prevProsp, prevState) {

        if (prevState.showOverlay
            && !this.state.showOverlay) {

            /**
             *  If we're removing an overlay, we're going to be playing
             * so let the API know
             */
            this.embedSubject.next({
                type: EmbedEvents.EMBED_PLAYER_READY,
            });

        }

    }

    componentWillUnmount() {

        this.subscriptions.forEach((subscription) => subscription.unsubscribe());

    }

    /**
     *
     * @param {array<object>} playlistData Array of objects (MediaItems - minimal contentId)
     * @param {string} initialContentId the initially selected clips contentId
     */
    createPlaylist(playlistData, initialContentId, topicId) {

        const {
            requestParams,
            customMetaDataLoader,
        } = this.props;

        return new Playlist(playlistData, initialContentId, topicId, this.mediaConfig, requestParams || {}, customMetaDataLoader);

    }

    /**
     *
     * @desc This will try to find the clip metadata passed in as a property of initialMedia.
     * It will look for `initialMedia.metaData` first, then `initialMedia.data`, if neither
     * are found it will return the passed in object to be added to the playlist.
     * @param {object} initialMedia The initial media metadata passed to the embed app
     *
     */
    findInitialMediaMetadata(initialMedia) {

        return _get(initialMedia, 'metaData', _get(initialMedia, 'data', initialMedia));

    }

    /**
     *
     * @desc Helper to find the initial contentId/slug of the clip. This will look for
     * `initialMediaMetaData.contentId` then `initialMediaMetaData.content_id` then `initialMediaMetaData.id`
     * @param {object} initialMediaMetaData The initial media metadata
     *
     */
    findInitialContentId(initialMediaMetaData) {

        return _get(initialMediaMetaData, 'contentId', _get(initialMediaMetaData, 'content_id', _get(initialMediaMetaData, 'id')));

    }

    initAutoPlay() {

        this.autoPlay = [
            true,
            'true',
            1,
            '1',
        ].indexOf(this.autoPlayParam) > -1 ? true : false;

    }

    initDevice() {

        this.deviceInfo = Device();
        this.isMobile = [
            'phone',
            'tablet',
        ].indexOf(this.deviceInfo.formFactor) > -1 ? true : false;

    }

    determinePlayerSize(width, height) {

        if (width >= 512 && width < 768) {

            return PlayerSizes.MED;

        } else if (width >= 768 && width < 830) {

            return PlayerSizes.LG;

        } else if (width >= 830) {

            return PlayerSizes.XLG;

        }

        return PlayerSizes.SM;

    }

    registerEmbedSubject(observationCenter, embedSubject) {

        observationCenter.embedSubject = embedSubject;

    }

    /**
     *
     * @param {object} metaData MediaItem metadata object
     * @param {object} mediaConfig The media config object
     *
     */
    createMediaItem(metaData, mediaConfig) {

        const CustomMetaDataLoader = _get(this.props, 'customMetaDataLoader');
        let mediaItem;

        if (CustomMetaDataLoader) {

            mediaItem = new CustomMetaDataLoader(metaData, mediaConfig);

        } else {

            mediaItem = new MLBMediaItem(metaData, mediaConfig);

        }

        return mediaItem;

    }

    /**
     *
     * @desc
     *
     */
    updatePlayList(message, mediaStarted, playing) {

        // Should be an AbstractMediaItem (MLBMediaItem for example)
        const playlistData = _cloneDeep(_get(message, 'playlistData'));
        const initialClipId = _get(message, 'currentClipId');
        const topicId = _get(message, 'topicId');

        try {

            // Ensure the topidId is set where needed
            if(topicId) {

                _set(this.props, 'mediaConfig.advertising.dfp.topicId', topicId);
                this.mediaConfig = this.setUpMediaConfig();

            }

            this.playlist = this.createPlaylist(playlistData, initialClipId, topicId);

            const embedSubject = _get(this, 'embedSubject', {
                next: () => {

                    console.error('embedSubject is not registered');
                    return null;

                },
            });

            // Send the new playlist to the player for updating
            embedSubject.next({
                type: EmbedEvents.PLAYLIST_UPDATED,
                playlist: this.playlist,
            });

            /** If there is a currentClipId, check for the selected item
             * and playback the new clip if it is different from the current clip id */
            if(initialClipId) {

                const newClip = this.playlist.getSelectedItem();

                // if we aren't playing, we need to reset the clip to be played
                if (!playing && newClip) {

                    this.setState({
                        clip: newClip,
                    });

                }

            }


        } catch(error) {

            console.log('There was a problem creating the playlist', error);

        }

    }

    subscribeToEmbedSubject() {

        const embedEvents = [
            EmbedEvents.SHOW_PLAYER,
            EmbedEvents.SHOW_CLICK_TO_PLAY,
            EmbedEvents.SET_CLIP_METADATA,
            EmbedEvents.UPDATE_PLAYLIST,
        ];
        const subscription = this.observationCenter.embedSubject
            .pipe(filter(({ type }) => embedEvents.includes(type)))
            .subscribe((message) => {

                const mediaPlayer = _get(this.state, 'mediaPlayer');
                const playing = _get(mediaPlayer, 'player.stateModel.playing', false);
                const mediaStarted = _get(mediaPlayer, 'player.stateModel.mediaStarted', false);
                const isAdPlaying = _get(mediaPlayer, 'player.stateModel.isAdPlaying', false);

                switch (message.type) {

                    case EmbedEvents.SHOW_PLAYER: {

                        if (this.state.showOverlay) {

                            this.setState({
                                clip: this.playlist.getSelectedItem(),
                                showOverlay: false,
                                wasClickToPlay: true,
                            });

                        }
                        break;

                    }
                    case EmbedEvents.SHOW_CLICK_TO_PLAY: {

                        this.setState({
                            showOverlay: true,
                            overlayType: OverlayTypes.CLICK_TO_PLAY,
                            wasClickToPlay: false,
                        });

                        this.mediaPlayerRef.current.stop();

                        break;

                    }
                    case EmbedEvents.SET_CLIP_METADATA: {

                        // Should be an AbstractMediaItem (MLBMediaItem for example)
                        const newMediaMetadata = _get(message, 'mediaItem');

                        this.playlist.setSelectedItem(newMediaMetadata.contentId);

                        // TODO: address this after basic use cases completed
                        this.setState({
                            showOverlay: false,
                            overlayType: null,
                            clip: newMediaMetadata,
                            wasClickToPlay: true,
                        });

                        this.embedSubject.next({
                            type: EmbedEvents.CLIP_METADATA_SET,
                            clip: newMediaMetadata,
                        });

                        break;

                    }
                    case EmbedEvents.UPDATE_PLAYLIST: {

                        this.updatePlayList(message, mediaStarted, playing);

                        break;

                    }

                }

            });

        this.subscriptions.push(subscription);

    }

    subscribeToPlayerSubject() {

        const PlayerEvents = MediaPlayerEvents.MEDIA_PLAYER;
        const Html5PlayerEvents = MediaPlayerEvents.HTML5;
        const playerEvents = [
            Html5PlayerEvents.MEDIA_LOADED_DATA,
            Html5PlayerEvents.MEDIA_START,
            PlayerEvents.PLAYER_RESIZED,
        ];
        const subscription = this.observationCenter.playerSubject
            .pipe(filter(({ type }) => {

                return playerEvents.includes(type);

            }))
            .subscribe((message) => {

                const mediaPlayer = _get(this.state, 'mediaPlayer');

                switch (message.type) {

                    case Html5PlayerEvents.MEDIA_START: {

                        if (this.state.showOverlay) {

                            this.observationCenter.embedSubject.next({ type: EmbedEvents.SHOW_PLAYER });

                        }
                        break;

                    }
                    case PlayerEvents.PLAYER_RESIZED: {

                        const {
                            width,
                            height,
                        } = message.data;
                        this.setState({
                            playerSize: this.determinePlayerSize(width, height),
                        });
                        break;

                    }

                }

            });

        this.subscriptions.push(subscription);

    }

    subscribeToPlayerStateSubject() {

        const Html5PlayerEvents = MediaPlayerEvents.HTML5;
        const playerStateEvents = [Messages.StateEventMessage.PLAYER_STATE_UPDATE];
        const subscription = this.observationCenter.stateSubject
            .pipe(filter(({ type }) => {

                return playerStateEvents.includes(type);

            }))
            .subscribe(({ stateModel }) => {

                const readyState = _get(stateModel, 'readyState', 0);

                // See https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState#value for readyState value details
                if(!this.state.showControls && readyState >= 2) {

                    this.setState({ showControls: true });

                }

            });

        this.subscriptions.push(subscription);

    }

    subscribeToControlsSubject() {

        const subscription = this.observationCenter.controlsSubject
            .pipe(filter((message) => {

                return message.type === ControlsEvents.STOP;

            })).subscribe((message) => {

                this.setState({
                    showOverlay: true,
                    overlayType: OverlayTypes.CLICK_TO_PLAY,
                    wasClickToPlay: false,
                });

            });

        this.subscriptions.push(subscription);

    }

    subscribeToPluginSubject() {

        const HTML5PlayerEvents = MediaPlayerEvents.HTML5;
        const pluginEvents = [
            AdvertisingPluginEvents.AD_LOADED,
            AdvertisingPluginEvents.AD_REQUEST_STARTED,
            AdvertisingPluginEvents.AD_START,
            AdvertisingPluginEvents.AD_ERROR,
            AdvertisingPluginEvents.AD_PLUGIN_ERROR,
            AdvertisingPluginEvents.AD_BLOCK_DETECTED,
            AdvertisingPluginEvents.AD_COMPLETE,
            HTML5PlayerEvents.CANPLAY,
            HTML5PlayerEvents.CANPLAYTHROUGH,
            HTML5PlayerEvents.RESUME,
        ];

        const subscription = this.observationCenter.pluginSubject.pipe(filter(({ type }) => pluginEvents
            .includes(type)))
            .subscribe((message) => {

                switch (message.type) {

                    case AdvertisingPluginEvents.AD_LOADED:
                    case AdvertisingPluginEvents.AD_REQUEST_STARTED:
                    case AdvertisingPluginEvents.AD_START:
                    case AdvertisingPluginEvents.AD_ERROR:
                    case AdvertisingPluginEvents.AD_PLUGIN_ERROR:
                    case AdvertisingPluginEvents.AD_BLOCK_DETECTED:
                    case AdvertisingPluginEvents.AD_COMPLETE: {

                        this.setState({
                            showControls: true,
                            wasClickToPlay: false,
                        });
                        break;

                    }

                    case HTML5PlayerEvents.CANPLAY:
                    case HTML5PlayerEvents.CANPLAYTHROUGH:
                    case HTML5PlayerEvents.RESUME: {

                        this.setState({
                            showControls: true,
                        });
                        break;

                    }

                }

            });

        this.subscriptions.push(subscription);

    }

    setUpMediaConfig() {

        const {
            mediaConfig,
            playerId,
        } = this.props;

        const playerMediaConfig = _cloneDeep(mediaConfig);
        _set(playerMediaConfig, 'pluginConfigs', _get(playerMediaConfig, 'pluginConfigs', []));

        // Get Communication Plugin Configs
        PluginConfigHelper.getEventReplicatorConfig(playerMediaConfig, playerId);
        PluginConfigHelper.getBandwagonConfig(playerMediaConfig, playerId);

        // Get Advertising Plugin Configs
        PluginConfigHelper.getAdvertisingPluginConfig(playerMediaConfig, playerId);

        // Get Analytics Plugin Configs
        PluginConfigHelper.getOmniturePluginConfig(playerMediaConfig, playerId);
        PluginConfigHelper.getChartBeatPluginConfig(playerMediaConfig, playerId);
        PluginConfigHelper.getConvivaPluginConfig(playerMediaConfig, playerId);

        playerMediaConfig.metadataServiceBase = this.props.metadataServiceBase;

        return playerMediaConfig;

    }

    showLeaveBehind() {

        this.setState({
            showOverlay: true,
            overlayType: OverlayTypes.LEAVE_BEHIND,
            wasClickToPlay: false,
        });

        // Inform subscribers of the new state
        this.observationCenter.embedSubject.next({
            type: EmbedEvents.SHOW_LEAVE_BEHIND,
            prevClip: this.state.clip,
            nextClip: this.playlist.getNextItem(),
        });

    }

    getNextItemMetadata(mediaItem) {

        let nextItemPromise;

        if (this.getNextItemMetadataOverride) {

            nextItemPromise = this.getNextItemMetadataOverride(mediaItem);

        } else {

            nextItemPromise = mediaItem.getMetaData();

        }

        nextItemPromise
            .then((result) => {

                this.setState({
                    clip: result,
                    showOverlay: false,
                    wasClickToPlay: true,
                });

            })
            .catch((error) => {

                console.error('ERROR: onClickNextClip', error);

            });

    }

    getMediaPlayerChildren() {

        const {
            showOverlay,
            overlayType,
            playerSize,
        } = this.state;
        // If we're in click to play, we won't have a clip on the state bobject
        const clip = this.playlist.getSelectedItem();

        if (!showOverlay) {

            return null;

        } else {

            switch (overlayType) {

                case OverlayTypes.CLICK_TO_PLAY: {

                    const selectedClip = this.playlist.getSelectedItem();

                    return <ClickToPlayOverlay
                        mediaConfig={this.mediaConfig}
                        onClick={this.onClickToPlay}
                        clip={clip}
                        playerSize={playerSize}
                    />;
                    break;

                }
                case OverlayTypes.LEAVE_BEHIND: {

                    return <LeaveBehindOverlay
                        mediaConfig={this.mediaConfig}
                        countdown={this.props.countdown}
                        playlist={this.playlist}
                        onClick={this.onOverlayClick}
                        onCountdownComplete={this.onCountdownComplete}
                        onClickNextClip={this.onClickNextClip}
                        onClickReplayClip={this.onClickReplayClip}
                        playerSize={playerSize}
                    />;
                    break;

                }

            }

        }

    }

    onClickToPlay() {

        // Get the selected item or the next item if selected doesn't exist
        const clipToPlay = this.playlist.getSelectedItem() || this.playlist.getNextItem();
        // Ensure the selected item exists.
        this.playlist.setSelectedItem(clipToPlay.contentId);

        this.observationCenter.embedSubject.next({ type: EmbedEvents.SHOW_PLAYER });

        /* this.setState({
            showOverlay: false,
            wasClickToPlay: true,
            clip: clipToPlay,
        }); */


        // const paused = _get(this.state, 'mediaPlayer.player.stateModel.paused', false);
        // if (paused) {
        //
        //     this.state.mediaPlayer.play();
        //
        // }

    }

    onClickReplayClip(mediaItem) {

        //const mediaPlayer = _get(this, 'mediaPlayer.current', { replay: () => null });
        if (!!this.mediaPlayerRef.current && !!this.state.clip) {

            this.setState({
                showOverlay: false,
                wasClickToPlay: true,
            });
            this.mediaPlayerRef.current.replay();
            this.observationCenter.embedSubject.next({
                type: EmbedEvents.REPLAY,
                prevClip: this.state.clip,
                nextClip: this.playlist.getNextItem(),
            });

        } else {

            this.setState({
                clip: mediaItem,
            });

        }

    }

    onCountdownComplete(mediaItem) {

        this.getNextItemMetadata(mediaItem);

        this.embedSubject.next({
            type: EmbedEvents.LEAVE_BEHIND_COUNTDOWN_COMPLETE,
            prevClip: this.state.clip,
            nextClip: mediaItem,
        });

    }

    onClickNextClip(mediaItem) {

        this.getNextItemMetadata(mediaItem);

        this.embedSubject.next({
            type: EmbedEvents.LEAVE_BEHIND_NEXT_CLIP_CLICK,
            prevClip: this.state.clip,
            nextClip: mediaItem,
        });

    }

    onMediaPlayerReady(mediaPlayer) {

        this.setState({ mediaPlayer }, () => {

            this.observationCenter = mediaPlayer.observationCenter;
            this.registerEmbedSubject(this.observationCenter, this.embedSubject);
            this.subscribeToEmbedSubject();
            this.subscribeToPlayerSubject();
            this.subscribeToPlayerStateSubject();
            this.subscribeToControlsSubject();
            this.subscribeToPluginSubject();
            this.props.onMediaPlayerReady(mediaPlayer);

        });

    }

    onMediaComplete() {

        // TODO: and maybe if replay is enabled
        if (!this.leaveBehindEnabled || this.playlist.length === 1) {

            const embedSubject = _get(this.state, 'mediaPlayer.observationCenter.embedSubject', { next: () => null });
            embedSubject.next({ type: EmbedEvents.SHOW_CLICK_TO_PLAY });

        } else if (this.playlist.length > 1) {

            this.showLeaveBehind();

        }

    }

    render() {

        const {
            clip,
            showOverlay,
            mediaPlayer,
            showControls,
            wasClickToPlay,
        } = this.state;
        const {
            fullscreenElementId,
            locale,
            webMediaApm,
            playerId,
            countdown,
            shouldPlayInline,
        } = this.props;

        return (
            <div className="app">
                <MediaPlayer
                    ref={this.mediaPlayerRef}
                    isVisible={!showOverlay}
                    clip={clip}
                    wasClickToPlay={wasClickToPlay}
                    autoplay={this.autoPlay}
                    shouldPlayInline={shouldPlayInline}
                    mute={this.mute}
                    mediaConfig={this.mediaConfig}
                    onMediaPlayerReady={this.onMediaPlayerReady}
                    onMediaComplete={this.onMediaComplete}
                    deviceInfo={this.deviceInfo}
                    webMediaApm={webMediaApm}
                    playerId={playerId}
                    locale={locale} >
                    {this.getMediaPlayerChildren()}
                </MediaPlayer>
                {(mediaPlayer !== undefined
                    && !showOverlay
                    && showControls)
                    ? <PlayerControls
                        deviceInfo={this.deviceInfo}
                        mediaPlayer={this.state.mediaPlayer}
                        playerWrapper={document.querySelector('#media-player')}
                        fullscreenElementId={fullscreenElementId}
                    />
                    : null
                }
            </div>
        );

    }

}

App.propTypes = {
    /** The media configuration object */
    mediaConfig: PropTypes.shape({ embed: PropTypes.object }),
    /** An object containing the request params such as autoPlay, mute etc. */
    requestParams: PropTypes.shape({
        query: PropTypes.object,
    }),
    /** The media playlist. A list of media items */
    list: PropTypes.arrayOf(PropTypes.object),
    /** The media playlist topic id if there is one */
    topicId: PropTypes.string,
    /** The id of the fullscreen target element. Defaults to "media-player" */
    fullscreenElementId: PropTypes.string,
    /** Hanlder to help you know when the component has been insterted into the DOM tree */
    onMediaPlayerReady: PropTypes.func,
    /** The unique id of the player */
    playerId: PropTypes.string,
    /** The value in seconds the leave behind will countdown for the next clip in the playlist (Default: 7) */
    countdown: PropTypes.number,
    shouldPlayInline: PropTypes.bool,
    metadataServiceBase: PropTypes.string,
    customMetaDataLoader: PropTypes.shape({
        getMetaData: PropTypes.func,
        parseData: PropTypes.func,
        data: PropTypes.string,
        requestParams: PropTypes.string,
        mediaConfig: PropTypes.string,
        locale: PropTypes.string,
        contentId: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        duration: PropTypes.string,
        tags: PropTypes.string,
        src: PropTypes.string,
        image: PropTypes.string,
        poster: PropTypes.string,
        date: PropTypes.string,
        isLive: PropTypes.bool,
    }),
};

App.defaultProps = {
    mediaConfig: {},
    requestParams: {
        query: {},
    },
    list: [],
    fullscreenElementId: 'media-player',
    onMediaPlayerReady: () => { },
    locale: 'en',
    playerId: 'media-player',
    countdown: 7,
    metadataServiceBase: '',
};

export default App;
